/**
 * Dubletten-Verwaltung für Olympus
 * Admin-Seite zum Erkennen und Zusammenführen von Dubletten
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Users, RefreshCw, Loader2, Check, X, AlertTriangle,
  ChevronDown, ChevronRight, Merge, Trash2, Eye, FileText, Calendar, MapPin
} from 'lucide-react';

const API_BASE = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

export default function DublettenPage() {
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [dubletten, setDubletten] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [selectedForMerge, setSelectedForMerge] = useState({});
  const [personDetails, setPersonDetails] = useState({});
  const [merging, setMerging] = useState(false);

  // Dubletten scannen
  const scanDubletten = async () => {
    setScanning(true);
    try {
      const response = await fetch(`${API_BASE}/api/dubletten/scan?min_aehnlichkeit=0.6&limit=100`);
      const data = await response.json();
      
      if (data.success) {
        setDubletten(data.gruppen || []);
        toast.success(`${data.anzahl_gruppen} Dubletten-Gruppen gefunden`);
      } else {
        toast.error(data.error || 'Scan fehlgeschlagen');
      }
    } catch (e) {
      console.error('Scan-Fehler:', e);
      toast.error('Fehler beim Scannen');
    } finally {
      setScanning(false);
    }
  };

  // Beim Laden initial scannen
  useEffect(() => {
    scanDubletten();
  }, []);

  // Person Details laden
  const loadPersonDetails = async (personId) => {
    if (personDetails[personId]) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/dubletten/details/${personId}`);
      const data = await response.json();
      
      if (data.success) {
        setPersonDetails(prev => ({
          ...prev,
          [personId]: data
        }));
      }
    } catch (e) {
      console.error('Details-Fehler:', e);
    }
  };

  // Gruppe expandieren/kollabieren
  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
    
    // Details für alle Personen in der Gruppe laden
    const group = dubletten.find(g => g.haupt_id === groupId);
    if (group) {
      loadPersonDetails(group.haupt_id);
      group.kandidaten.forEach(k => loadPersonDetails(k.id));
    }
  };

  // Person für Merge auswählen
  const toggleMergeSelection = (groupId, personId) => {
    setSelectedForMerge(prev => {
      const group = prev[groupId] || { behalten: null, loeschen: [] };
      
      if (group.behalten === personId) {
        // Deselektieren
        return { ...prev, [groupId]: { ...group, behalten: null } };
      } else if (group.loeschen.includes(personId)) {
        // Aus Löschen-Liste entfernen
        return { ...prev, [groupId]: { ...group, loeschen: group.loeschen.filter(id => id !== personId) } };
      } else if (!group.behalten) {
        // Als "Behalten" markieren
        return { ...prev, [groupId]: { ...group, behalten: personId } };
      } else {
        // Zur Löschen-Liste hinzufügen
        return { ...prev, [groupId]: { ...group, loeschen: [...group.loeschen, personId] } };
      }
    });
  };

  // Zusammenführen ausführen
  const executeMerge = async (groupId) => {
    const selection = selectedForMerge[groupId];
    if (!selection?.behalten || selection.loeschen.length === 0) {
      toast.error('Bitte wählen Sie eine Person zum Behalten und mindestens eine zum Löschen');
      return;
    }

    setMerging(true);
    try {
      const response = await fetch(`${API_BASE}/api/dubletten/zusammenfuehren`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          behalten_id: selection.behalten,
          loeschen_ids: selection.loeschen
        })
      });
      const data = await response.json();
      
      if (data.success) {
        toast.success(`${data.geloescht} Dublette(n) zusammengeführt! ${data.uebertragen.dokumente} Dokumente, ${data.uebertragen.aufgaben} Aufgaben übertragen.`);
        // Neu scannen
        scanDubletten();
        // Auswahl zurücksetzen
        setSelectedForMerge(prev => ({ ...prev, [groupId]: { behalten: null, loeschen: [] } }));
      } else {
        toast.error(data.error || 'Zusammenführung fehlgeschlagen');
      }
    } catch (e) {
      console.error('Merge-Fehler:', e);
      toast.error('Fehler beim Zusammenführen');
    } finally {
      setMerging(false);
    }
  };

  // Render Person Card
  const renderPersonCard = (person, groupId, isMain = false) => {
    const details = personDetails[person.id || person.haupt_id];
    const personId = person.id || person.haupt_id;
    const selection = selectedForMerge[groupId] || { behalten: null, loeschen: [] };
    const isBehalten = selection.behalten === personId;
    const isLoeschen = selection.loeschen.includes(personId);
    
    return (
      <div 
        key={personId}
        className={`p-3 rounded-lg border-2 transition-all cursor-pointer ${
          isBehalten ? 'border-green-500 bg-green-50' :
          isLoeschen ? 'border-red-500 bg-red-50' :
          'border-slate-200 bg-white hover:border-slate-300'
        }`}
        onClick={() => toggleMergeSelection(groupId, personId)}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="font-medium">
                {person.vorname || ''} {person.nachname || person.haupt_name || ''}
              </span>
              {isMain && <Badge variant="outline" className="text-xs">Haupt</Badge>}
              {person.typ && (
                <Badge variant="secondary" className="text-xs">
                  {person.typ === 'klient' ? 'Betreuter' : 'Person'}
                </Badge>
              )}
            </div>
            
            {person.geburtsdatum && (
              <div className="flex items-center gap-1 text-xs text-slate-500 mt-1">
                <Calendar className="w-3 h-3" />
                {person.geburtsdatum}
              </div>
            )}
            
            {person.aehnlichkeit !== undefined && (
              <div className="mt-1">
                <Badge className={`text-xs ${
                  person.aehnlichkeit >= 0.9 ? 'bg-red-100 text-red-700' :
                  person.aehnlichkeit >= 0.7 ? 'bg-amber-100 text-amber-700' :
                  'bg-slate-100 text-slate-700'
                }`}>
                  {Math.round(person.aehnlichkeit * 100)}% Übereinstimmung
                </Badge>
                {person.gruende?.length > 0 && (
                  <p className="text-xs text-slate-500 mt-1">
                    {person.gruende.join(', ')}
                  </p>
                )}
              </div>
            )}
            
            {/* Details wenn geladen */}
            {details && (
              <div className="mt-2 pt-2 border-t border-slate-100 text-xs text-slate-500">
                <div className="flex gap-4">
                  <span className="flex items-center gap-1">
                    <FileText className="w-3 h-3" />
                    {details.verknuepfungen?.dokumente || 0} Dokumente
                  </span>
                  <span className="flex items-center gap-1">
                    <Check className="w-3 h-3" />
                    {details.verknuepfungen?.aufgaben || 0} Aufgaben
                  </span>
                  <span>
                    {details.verknuepfungen?.massnahmen || 0} Maßnahmen
                  </span>
                </div>
                {details.person?.strasse && (
                  <div className="flex items-center gap-1 mt-1">
                    <MapPin className="w-3 h-3" />
                    {details.person.strasse} {details.person.hausnummer}, {details.person.plz} {details.person.ort}
                  </div>
                )}
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-1">
            {isBehalten && (
              <Badge className="bg-green-500 text-white">
                <Check className="w-3 h-3 mr-1" /> Behalten
              </Badge>
            )}
            {isLoeschen && (
              <Badge className="bg-red-500 text-white">
                <Trash2 className="w-3 h-3 mr-1" /> Löschen
              </Badge>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="dubletten-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users className="w-6 h-6 text-amber-500" />
            Dubletten-Verwaltung
          </h1>
          <p className="text-slate-500 mt-1">
            Erkennen und Zusammenführen von doppelten Personen-Einträgen
          </p>
        </div>
        
        <Button 
          onClick={scanDubletten} 
          disabled={scanning}
          className="bg-amber-500 hover:bg-amber-600"
        >
          {scanning ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Scanne...</>
          ) : (
            <><RefreshCw className="w-4 h-4 mr-2" /> Neu scannen</>
          )}
        </Button>
      </div>

      {/* Anleitung */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-blue-500 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-blue-700">So funktioniert's:</p>
              <ol className="list-decimal ml-4 mt-1 text-blue-600 space-y-1">
                <li>Klicken Sie auf eine Gruppe um die Details zu sehen</li>
                <li>Klicken Sie <strong>zuerst</strong> auf die Person die <strong>behalten</strong> werden soll (wird grün)</li>
                <li>Klicken Sie dann auf alle Personen die <strong>gelöscht</strong> werden sollen (werden rot)</li>
                <li>Klicken Sie auf "Zusammenführen" - alle Dokumente, Aufgaben etc. werden übertragen</li>
              </ol>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Statistik */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4 text-center">
            <p className="text-3xl font-bold text-amber-500">{dubletten.length}</p>
            <p className="text-sm text-slate-500">Dubletten-Gruppen</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <p className="text-3xl font-bold text-red-500">
              {dubletten.reduce((sum, g) => sum + g.anzahl, 0)}
            </p>
            <p className="text-sm text-slate-500">Betroffene Einträge</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <p className="text-3xl font-bold text-green-500">
              {dubletten.filter(g => g.kandidaten.some(k => k.aehnlichkeit >= 0.9)).length}
            </p>
            <p className="text-sm text-slate-500">Hohe Übereinstimmung (≥90%)</p>
          </CardContent>
        </Card>
      </div>

      {/* Dubletten-Liste */}
      {dubletten.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Check className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <p className="text-lg font-medium text-green-700">Keine Dubletten gefunden!</p>
            <p className="text-slate-500">Die Datenbank enthält keine offensichtlichen Duplikate.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {dubletten.map((gruppe, index) => {
            const isExpanded = expandedGroups[gruppe.haupt_id];
            const selection = selectedForMerge[gruppe.haupt_id] || { behalten: null, loeschen: [] };
            const canMerge = selection.behalten && selection.loeschen.length > 0;
            
            return (
              <Card key={gruppe.haupt_id} className="overflow-hidden">
                <CardHeader 
                  className="cursor-pointer hover:bg-slate-50 transition-colors py-3"
                  onClick={() => toggleGroup(gruppe.haupt_id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {isExpanded ? (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      )}
                      <CardTitle className="text-base">
                        {gruppe.haupt_name}
                      </CardTitle>
                      <Badge variant="outline">
                        {gruppe.anzahl} Einträge
                      </Badge>
                      {gruppe.kandidaten.some(k => k.aehnlichkeit >= 0.9) && (
                        <Badge className="bg-red-100 text-red-700">Hohe Übereinstimmung</Badge>
                      )}
                    </div>
                    
                    {isExpanded && canMerge && (
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                        onClick={(e) => {
                          e.stopPropagation();
                          executeMerge(gruppe.haupt_id);
                        }}
                        disabled={merging}
                      >
                        {merging ? (
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                          <Merge className="w-4 h-4 mr-1" />
                        )}
                        Zusammenführen
                      </Button>
                    )}
                  </div>
                </CardHeader>
                
                {isExpanded && (
                  <CardContent className="pt-0 pb-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {/* Hauptperson */}
                      {renderPersonCard(gruppe, gruppe.haupt_id, true)}
                      
                      {/* Kandidaten */}
                      {gruppe.kandidaten.map(kandidat => 
                        renderPersonCard(kandidat, gruppe.haupt_id, false)
                      )}
                    </div>
                    
                    {canMerge && (
                      <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                        <p className="font-medium text-amber-700">
                          Zusammenführung vorbereitet:
                        </p>
                        <p className="text-amber-600">
                          "{personDetails[selection.behalten]?.person?.vorname} {personDetails[selection.behalten]?.person?.nachname}" wird behalten.
                          {selection.loeschen.length} Eintrag/Einträge werden gelöscht und ihre Daten übertragen.
                        </p>
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}
      
      {/* Erinnerung */}
      <Card className="bg-purple-50 border-purple-200 mt-8">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <FileText className="w-5 h-5 text-purple-500 mt-0.5" />
            <div className="text-sm text-purple-700">
              <p className="font-medium">Erinnerung: Formular-Test ausstehend</p>
              <p>Laden Sie ein echtes Formular (z.B. Wohngeldantrag) hoch und testen Sie die "Formular analysieren" Funktion im Posteingang.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
